import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";
import { EASE, Reveal } from "@/components/Reveal";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Field, Honeypot, inputCls } from "@/components/forms";

const TYPE_OPTIONS = [
  { v: "invited", label: "I was invited by Syndeō" },
  { v: "nominated", label: "I was nominated or recommended" },
  { v: "self", label: "I am submitting myself for consideration" },
  { v: "recommending", label: "I am recommending someone else" },
];

const KNOW_OPTIONS = [
  "I have personally used them",
  "My family or household has used them",
  "My family office or team has worked with them",
  "I have referred others to them",
  "Professional relationship",
  "Other",
];

const AGAIN_OPTIONS = ["Absolutely", "Yes", "With certain considerations"];

const BUSINESS_OPTIONS = [
  "Client referrals",
  "Professional referrals",
  "Family office / advisor referrals",
  "Institutional relationships",
  "Other",
];

const initial = {
  submission_type: "",
  inviter_name: "",
  nominator_name: "",
  nominator_email: "",
  relationship_basis: "",
  provider_name: "",
  area_of_expertise: "",
  location_markets: "",
  website_url: "",
  contact: "",
  why_exceptional: "",
  recommend_for: "",
  would_recommend_again: "",
  about_work: "",
  distinguishes: "",
  best_suited: "",
  business_generated: "",
  references_text: "",
  recommender_name: "",
  recommender_email: "",
  may_contact: false,
  website: "",
};

const SectionLabel = ({ n, title }: { n: string; title: string }) => (
  <div className="flex items-baseline gap-4 border-b border-stone/50 pb-4">
    <span className="font-garamond text-3xl leading-none text-stone">{n}</span>
    <h2 className="font-oswald text-xs uppercase tracking-[0.35em] text-marine">
      {title}
    </h2>
  </div>
);

const RadioRow = ({
  name,
  value,
  current,
  onChange,
  label,
  testId,
}: {
  name: string;
  value: string;
  current: string;
  onChange: (v: string) => void;
  label: string;
  testId: string;
}) => (
  <label
    className={`flex cursor-pointer items-center gap-3 border px-4 py-3 transition-colors duration-300 ${
      current === value
        ? "border-marine bg-chalk"
        : "border-stone/60 hover:border-stone"
    }`}
  >
    <input
      type="radio"
      name={name}
      data-testid={testId}
      value={value}
      checked={current === value}
      onChange={() => onChange(value)}
      className="h-4 w-4 shrink-0 accent-[#263532]"
    />
    <span className="font-garamond text-base text-softblack">{label}</span>
  </label>
);

const RadioGroup = ({
  label,
  name,
  options,
  current,
  onChange,
  testPrefix,
}: {
  label: string;
  name: string;
  options: string[];
  current: string;
  onChange: (v: string) => void;
  testPrefix: string;
}) => (
  <div>
    <span className="mb-3 block font-oswald text-[11px] uppercase tracking-[0.3em] text-marine">
      {label}
    </span>
    <div className="space-y-3">
      {options.map((o) => (
        <RadioRow
          key={o}
          name={name}
          value={o}
          current={current}
          onChange={onChange}
          label={o}
          testId={`${testPrefix}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        />
      ))}
    </div>
  </div>
);

const Note = ({ children }: { children: string }) => (
  <p className="mb-3 font-garamond text-base italic leading-relaxed text-softblack/70">
    {children}
  </p>
);

export default function Professionals() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (key: string) => (e: any) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const setVal = (key: string) => (v: string) =>
    setForm((f) => ({ ...f, [key]: v }));

  const isSelfPath = ["invited", "nominated", "self"].includes(form.submission_type);
  const isRecommending = form.submission_type === "recommending";
  const typeChosen = form.submission_type !== "";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const nul = (v: string) => (v ? v : null);
    try {
      await api.post("/recommendations", {
        ...form,
        inviter_name: nul(form.inviter_name),
        nominator_name: nul(form.nominator_name),
        nominator_email: nul(form.nominator_email),
        relationship_basis: nul(form.relationship_basis),
        website_url: nul(form.website_url),
        contact: nul(form.contact),
        why_exceptional: nul(form.why_exceptional),
        recommend_for: nul(form.recommend_for),
        would_recommend_again: nul(form.would_recommend_again),
        about_work: nul(form.about_work),
        distinguishes: nul(form.distinguishes),
        best_suited: nul(form.best_suited),
        business_generated: nul(form.business_generated),
        references_text: nul(form.references_text),
        recommender_name: nul(form.recommender_name),
        recommender_email: nul(form.recommender_email),
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err: any) {
      const status = err?.response?.status;
      setError(
        status === 429
          ? "Too many attempts. Please wait a few minutes and try again."
          : status === 422
            ? "Please complete every required field before submitting."
            : "Something went quiet on our end. Please try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="professionals-page" className="min-h-screen">
      <SiteHeader />
      <main className="mx-auto max-w-xl px-6 pb-28 pt-36">
        {done ? (
          <motion.div
            data-testid="submission-confirmation"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="pt-16 text-center"
          >
            <img
              src="/assets/o-mark.jpeg"
              alt="ō"
              className="mx-auto h-12 w-12 rounded-full object-cover"
            />
            <div className="mx-auto mt-8 h-px w-10 bg-ochre" />
            <h1 className="mt-8 font-garamond text-4xl text-ink">
              Submission received.
            </h1>
            <p className="mx-auto mt-6 max-w-md font-garamond text-lg leading-relaxed text-softblack/85">
              Thank you. Every submission is independently reviewed by
              Syndeō. Submission does not guarantee inclusion.
            </p>
            <Link
              to="/"
              data-testid="submission-return-home"
              className="mt-12 inline-block border-b border-ink pb-1 font-oswald text-[11px] uppercase tracking-[0.3em] text-ink transition-colors hover:border-walnut hover:text-walnut"
            >
              Return to syndeō
            </Link>
          </motion.div>
        ) : (
          <>
            <Reveal>
              <p className="font-oswald text-[11px] uppercase tracking-[0.4em] text-seaglass">
                Professional Submission
              </p>
              <h1 className="mt-4 font-garamond text-4xl text-ink sm:text-5xl">
                Professional Consideration
              </h1>
              <p className="mt-6 font-garamond text-lg leading-relaxed text-softblack/85">
                Exceptional professionals are often discovered through
                firsthand experience and trusted recommendations.
                Professionals may be recommended, invited, or submit
                themselves for consideration.
              </p>
              <p className="mt-4 font-garamond text-lg leading-relaxed text-softblack/85">
                Every submission is independently reviewed by Syndeō.
                Submission does not guarantee inclusion.
              </p>
            </Reveal>

            <form
              data-testid="submission-form"
              onSubmit={submit}
              className="relative mt-14 space-y-12"
            >
              <Honeypot
                value={form.website}
                onChange={(v) => setForm((f) => ({ ...f, website: v }))}
              />

              <section className="space-y-8">
                <SectionLabel n="01" title="The Submission" />
                <div>
                  <span className="mb-3 block font-oswald text-[11px] uppercase tracking-[0.3em] text-marine">
                    How are you submitting this professional? *
                  </span>
                  <div className="space-y-3">
                    {TYPE_OPTIONS.map((opt) => (
                      <RadioRow
                        key={opt.v}
                        name="submission_type"
                        value={opt.v}
                        current={form.submission_type}
                        onChange={setVal("submission_type")}
                        label={opt.label}
                        testId={`sub-type-${opt.v}`}
                      />
                    ))}
                  </div>
                </div>
              </section>

              <AnimatePresence>
                {typeChosen && (
                  <motion.div
                    key={form.submission_type}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="space-y-12"
                  >
                    {form.submission_type === "invited" && (
                      <section className="space-y-8">
                        <SectionLabel n="02" title="Your Invitation" />
                        <div>
                          <span className="mb-2 block font-oswald text-[11px] uppercase tracking-[0.3em] text-marine">
                            Who invited you?
                          </span>
                          <Note>
                            If you know the name of the person who contacted
                            you, please include it below.
                          </Note>
                          <input
                            data-testid="sub-inviter-name"
                            value={form.inviter_name}
                            onChange={set("inviter_name")}
                            className={inputCls}
                          />
                        </div>
                      </section>
                    )}

                    {form.submission_type === "nominated" && (
                      <section className="space-y-8">
                        <SectionLabel n="02" title="Your Nomination" />
                        <div>
                          <span className="mb-2 block font-oswald text-[11px] uppercase tracking-[0.3em] text-marine">
                            Who recommended or nominated you?
                          </span>
                          <Note>
                            If you were referred by a Syndeō member or another
                            individual, please share their name.
                          </Note>
                          <div className="space-y-6">
                            <input
                              data-testid="sub-nominator-name"
                              placeholder="Name"
                              value={form.nominator_name}
                              onChange={set("nominator_name")}
                              className={inputCls}
                            />
                            <input
                              type="email"
                              data-testid="sub-nominator-email"
                              placeholder="Email (optional)"
                              value={form.nominator_email}
                              onChange={set("nominator_email")}
                              className={inputCls}
                            />
                          </div>
                        </div>
                      </section>
                    )}

                    <section className="space-y-8">
                      <SectionLabel
                        n={
                          isRecommending || form.submission_type === "self"
                            ? "02"
                            : "03"
                        }
                        title={isRecommending ? "The Professional" : "Your Practice"}
                      />
                      {isRecommending && (
                        <RadioGroup
                          label="How do you know this professional? *"
                          name="relationship_basis"
                          options={KNOW_OPTIONS}
                          current={form.relationship_basis}
                          onChange={setVal("relationship_basis")}
                          testPrefix="sub-know"
                        />
                      )}
                      <Field
                        label="Professional / Firm name"
                        htmlFor="provider_name"
                      >
                        <input
                          id="provider_name"
                          data-testid="sub-provider-name"
                          required
                          value={form.provider_name}
                          onChange={set("provider_name")}
                          className={inputCls}
                        />
                      </Field>
                      <Field
                        label="Area of expertise"
                        htmlFor="area_of_expertise"
                      >
                        <input
                          id="area_of_expertise"
                          data-testid="sub-expertise"
                          required
                          value={form.area_of_expertise}
                          onChange={set("area_of_expertise")}
                          className={inputCls}
                        />
                      </Field>
                      <Field
                        label="Location / Markets served"
                        htmlFor="location_markets"
                      >
                        <input
                          id="location_markets"
                          data-testid="sub-location"
                          required
                          value={form.location_markets}
                          onChange={set("location_markets")}
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Website" htmlFor="website_url" optional>
                        <input
                          id="website_url"
                          type="url"
                          data-testid="sub-website"
                          value={form.website_url}
                          onChange={set("website_url")}
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Email or phone" htmlFor="contact" optional>
                        <input
                          id="contact"
                          data-testid="sub-contact"
                          value={form.contact}
                          onChange={set("contact")}
                          className={inputCls}
                        />
                      </Field>
                    </section>

                    {isRecommending && (
                      <section className="space-y-8">
                        <SectionLabel n="03" title="Your Experience" />
                        <div>
                          <span className="mb-2 block font-oswald text-[11px] uppercase tracking-[0.3em] text-marine">
                            What makes this professional exceptional? *
                          </span>
                          <Note>
                            Tell us what distinguishes them based on your
                            experience.
                          </Note>
                          <textarea
                            data-testid="sub-why-exceptional"
                            required
                            rows={4}
                            value={form.why_exceptional}
                            onChange={set("why_exceptional")}
                            className={inputCls}
                          />
                        </div>
                        <div>
                          <span className="mb-2 block font-oswald text-[11px] uppercase tracking-[0.3em] text-marine">
                            What specifically would you recommend them for? *
                          </span>
                          <Note>
                            Specific use cases are particularly helpful.
                          </Note>
                          <textarea
                            data-testid="sub-recommend-for"
                            required
                            rows={4}
                            value={form.recommend_for}
                            onChange={set("recommend_for")}
                            className={inputCls}
                          />
                        </div>
                        <RadioGroup
                          label="Would you personally use or recommend them again? *"
                          name="would_recommend_again"
                          options={AGAIN_OPTIONS}
                          current={form.would_recommend_again}
                          onChange={setVal("would_recommend_again")}
                          testPrefix="sub-again"
                        />
                      </section>
                    )}

                    {isSelfPath && (
                      <section className="space-y-8">
                        <SectionLabel
                          n={form.submission_type === "self" ? "03" : "04"}
                          title="Your Work"
                        />
                        <div>
                          <span className="mb-2 block font-oswald text-[11px] uppercase tracking-[0.3em] text-marine">
                            Tell us about your work. *
                          </span>
                          <Note>
                            Briefly describe your area of expertise and the
                            clients or situations you are particularly well
                            suited to serve.
                          </Note>
                          <textarea
                            data-testid="sub-about-work"
                            required
                            rows={4}
                            value={form.about_work}
                            onChange={set("about_work")}
                            className={inputCls}
                          />
                        </div>
                        <Field
                          label="What distinguishes your practice or firm?"
                          htmlFor="distinguishes"
                        >
                          <textarea
                            id="distinguishes"
                            data-testid="sub-distinguishes"
                            required
                            rows={4}
                            value={form.distinguishes}
                            onChange={set("distinguishes")}
                            className={inputCls}
                          />
                        </Field>
                        <Field
                          label="Who are you best suited to help?"
                          htmlFor="best_suited"
                        >
                          <textarea
                            id="best_suited"
                            data-testid="sub-best-suited"
                            required
                            rows={4}
                            value={form.best_suited}
                            onChange={set("best_suited")}
                            className={inputCls}
                          />
                        </Field>
                        <RadioGroup
                          label="How is most of your business currently generated? *"
                          name="business_generated"
                          options={BUSINESS_OPTIONS}
                          current={form.business_generated}
                          onChange={setVal("business_generated")}
                          testPrefix="sub-business"
                        />
                        <div>
                          <span className="mb-2 block font-oswald text-[11px] uppercase tracking-[0.3em] text-marine">
                            Professional references{" "}
                            <span className="normal-case tracking-normal text-stone">
                              (optional)
                            </span>
                          </span>
                          <Note>
                            You may provide the names of clients, advisors, or
                            other professionals who can speak to your work.
                            Please only provide contact information where you
                            have permission to do so.
                          </Note>
                          <textarea
                            data-testid="sub-references"
                            rows={4}
                            value={form.references_text}
                            onChange={set("references_text")}
                            className={inputCls}
                          />
                        </div>
                      </section>
                    )}

                    {isRecommending && (
                      <section className="space-y-8">
                        <SectionLabel n="04" title="About You" />
                        <Field label="Your name" htmlFor="recommender_name">
                          <input
                            id="recommender_name"
                            data-testid="sub-recommender-name"
                            required
                            value={form.recommender_name}
                            onChange={set("recommender_name")}
                            className={inputCls}
                          />
                        </Field>
                        <Field label="Your email" htmlFor="recommender_email">
                          <input
                            id="recommender_email"
                            type="email"
                            data-testid="sub-recommender-email"
                            required
                            value={form.recommender_email}
                            onChange={set("recommender_email")}
                            className={inputCls}
                          />
                        </Field>
                        <label className="flex cursor-pointer items-center gap-3 font-garamond text-base text-softblack">
                          <input
                            type="checkbox"
                            data-testid="sub-may-contact"
                            checked={form.may_contact}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                may_contact: e.target.checked,
                              }))
                            }
                            className="h-4 w-4 accent-[#263532]"
                          />
                          You may contact me to learn more about this
                          recommendation.
                        </label>
                      </section>
                    )}

                    {error && (
                      <p
                        data-testid="submission-error"
                        className="border border-walnut/40 bg-chalk px-4 py-3 font-garamond text-base text-walnut"
                      >
                        {error}
                      </p>
                    )}

                    <div className="pt-2">
                      <button
                        type="submit"
                        data-testid="submission-submit"
                        disabled={submitting}
                        className="border border-ink px-10 py-4 font-oswald text-xs uppercase tracking-[0.35em] text-ink transition-colors duration-300 hover:bg-ink hover:text-paper disabled:opacity-50"
                      >
                        {submitting ? "Submitting…" : "Submit for Consideration"}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
